import { useQuery } from '@tanstack/react-query';
import type { PassportPublication, PassportQrTokenList } from '@siliconx/shared';
import { api, throwIfNotOk } from './api';
import { qk } from './queryKeys';

/**
 * SIL-2409 (PR 5): owner-side reads of the public buyer passport. The API routes live in
 * apps/api/src/routes/client/passport-qr.ts; both are keyed by the asset id that currently holds the token / publication.
 */

/** Lifecycle states in which the toggle is not offered (the sale / disposal hook already made the passport private). */
export const PASSPORT_TOGGLE_HIDDEN_STATES: readonly string[] = ['sold', 'disposed'];

export function passportToggleHidden(lifecycleStateId: string | null | undefined): boolean {
  return lifecycleStateId != null && PASSPORT_TOGGLE_HIDDEN_STATES.includes(lifecycleStateId);
}

export async function fetchPassportPublication(assetId: string): Promise<PassportPublication> {
  const res = await api.api.client.passport[':assetId'].publication.$get({ param: { assetId } });
  if (!res.ok) {
    await throwIfNotOk(res);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as unknown as PassportPublication;
}

export async function setPassportPublished(
  assetId: string,
  published: boolean,
  reason: 'owner_toggle' | 'listing_end' = 'owner_toggle',
): Promise<PassportPublication> {
  const res = await api.api.client.passport[':assetId'].publication.$put({
    param: { assetId },
    json: { published, reason },
  });
  if (!res.ok) {
    await throwIfNotOk(res);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as unknown as PassportPublication;
}

export function usePassportPublication(assetId: string, enabled = true) {
  return useQuery<PassportPublication, Error>({
    queryKey: qk.assets.passportPublication(assetId),
    queryFn: () => fetchPassportPublication(assetId),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function usePassportQrTokens(assetId: string, enabled = true) {
  return useQuery<PassportQrTokenList, Error>({
    queryKey: qk.assets.passportQrTokens(assetId),
    queryFn: async () => {
      const res = await api.api.client.passport[':assetId']['qr-tokens'].$get({ param: { assetId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as PassportQrTokenList;
    },
    enabled,
    retry: false,
  });
}

const ERROR_COPY: Record<string, string> = {
  taken_down: 'This passport was taken down by operations. Contact support to have it restored.',
  forbidden: 'Only an organization admin can change this.',
  busy: 'The asset is busy. Try again in a moment.',
  invalid: 'That change is not valid for this asset.',
  token_cap_reached: 'This asset already has the maximum number of share links. Revoke one first.',
  no_owner: 'This asset has no owner.',
  // SIL-2423: rolling 24 h per-asset cap on issuing share links / publishing (the API sends Retry-After)
  daily_limit: 'Daily limit reached for this asset. Try again later.',
  // SIL-2439: a system and its components are never public together
  parent_public: 'The system passport is public. Make it private before publishing this component on its own.',
  child_public: 'Components of this system are public on their own. Make them private before publishing the system.',
};

/** Fixed customer copy for an API error carrying `{ error: { code } }` (see `throwIfNotOk`). */
export function passportErrorMessage(err: unknown): string {
  const code = (err as { body?: { error?: { code?: string } } } | null)?.body?.error?.code;
  return (code && ERROR_COPY[code]) || 'Something went wrong. Try again.';
}

/** Fixed copy for the create-listing response's `passportPublishReason`. */
export function passportPublishReasonMessage(reason: string | undefined): string {
  switch (reason) {
    case 'taken_down':
      return 'The passport could not be made public: it was taken down by operations.';
    case 'not_permitted':
      return 'The passport could not be made public: only an organization admin can do that.';
    case 'busy':
      return 'The passport could not be made public right now. You can turn it on from the asset page.';
    case 'rate_limited':
      return 'The passport could not be made public: this asset reached its daily publish limit. Try again later from the asset page.';
    case 'child_public':
      return 'The passport could not be made public: components of this system are public on their own. Make them private first, then turn it on from the asset page.';
    case 'parent_public':
      return 'The passport could not be made public: the system this component is installed in is public.';
    default:
      return 'The passport could not be made public. You can turn it on from the asset page.';
  }
}
